import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslation } from "../../core/translation.core";
import orderCore from "../../core/order.core";
import { Order } from "../../types/order";
import { LabelAndTextArea } from "../reuseables/form.comp";
import EditIcon from "../../icons/edit.icon";

interface DeliveryNoteEditorProps {
  order: Order;
}

interface DeliveryNoteFormData {
  deliveryNote: string;
}

export default function DeliveryNoteEditor({ order }: DeliveryNoteEditorProps) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<DeliveryNoteFormData>({
    defaultValues: {
      deliveryNote: order.deliveryNote || "",
    },
  });

  const handleSave = async (data: DeliveryNoteFormData) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await orderCore.updateDeliveryNote(order.id, data.deliveryNote, queryClient);
      toast.success(t(msg.import.success));
      setIsEditing(false);
    } catch (error) {
      toast.error(t(msg.errors.contactAdmin));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    methods.reset({
      deliveryNote: order.deliveryNote || "",
    });
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <div className="delivery-note-editor flex items-center gap-2 text-sm">
        <span className="text-gray-600 dark:text-gray-400">{t(msg.delivery.deliveryNote)}:</span>
        <div className="delivery-node-content flex-1 min-h-[1.25rem] whitespace-pre-wrap">
          {order.deliveryNote || <span className="text-gray-400 italic">{t(msg.delivery.noDeliveryNote)}</span>}
        </div>
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="edit-note flex items-center justify-center w-6 h-6 text-her-blue hover:bg-her-blue/10 rounded transition-colors"
          title={t(msg.delivery.editDeliveryNote)}
        >
          <EditIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={methods.handleSubmit(handleSave)} className="delivery-note-editor-form space-y-2 text-sm">
      <LabelAndTextArea
        register={methods.register}
        label={t(msg.delivery.deliveryNote)}
        fieldName="deliveryNote"
        rows={3}
        topLabelCss="block"
        labelCss="text-gray-600 dark:text-gray-400 mb-1"
        errors={methods.formState.errors}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isLoading}
          className="px-3 py-1 bg-her-blue text-white rounded hover:bg-her-blue/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? t(msg.form.saving) : t(msg.form.save)}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={isLoading}
          className="px-3 py-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t(msg.form.cancel)}
        </button>
      </div>
    </form>
  );
}
